import { type FC, type ReactNode, type PropsWithChildren, useMemo } from 'react';

import { Button } from '@vakantiesnl/components/src/atoms/Button';
import { Icon } from '@vakantiesnl/components/src/atoms/Icon';
import { TrustPilot } from '@vakantiesnl/components/src/atoms/TrustPilot';
import { Typography } from '@vakantiesnl/components/src/atoms/Typography';
import ImageComponent from '@vakantiesnl/components/src/molecules/ImageComponent';
import { TabsBar } from '@vakantiesnl/components/src/molecules/TabsBar';
import { useMicroCopyContext } from '@vakantiesnl/services/src/context/microCopyContext';
import { useAsset } from '@vakantiesnl/services/src/hooks/contentful';

import { useCustomStyles } from './OfferSummaryWrapper.style';

type OfferSummaryWrapperProps = {
	checkAvailability?: VoidFunction;
	isCheckingAvailability?: boolean;
	isBookingStatus?: boolean;
	tourOperator?: string;
	isAccoOnlyOffer?: boolean;
	/** Callback onChange selected tab **/
	onTabChange?: (tabIndex: number, tabCaption?: string) => void;
} & (
	| {
			isTabView: true;
			priceOverviewChildren: ReactNode;
			tripOverviewChildren: ReactNode;
	  }
	| {
			isTabView?: false;
			priceOverviewChildren?: ReactNode;
			tripOverviewChildren?: ReactNode;
	  }
);

export const DEFAULT_OFFER_SUMMARY_TAB = 'priceOverview';

/** Wrapping the offer summary on mobile with tab layout and availability checker button */
export const OfferSummaryWrapper: FC<PropsWithChildren<OfferSummaryWrapperProps>> = ({
	priceOverviewChildren,
	tripOverviewChildren,
	checkAvailability,
	isCheckingAvailability,
	onTabChange,
	isTabView,
	children,
	tourOperator,
	isAccoOnlyOffer = false,
}) => {
	const { classes, cx } = useCustomStyles();
	const microCopies = useMicroCopyContext();
	const tourOperatorAssetId = microCopies[`touroperator.${tourOperator}.image.asset`];
	const asset = useAsset(tourOperatorAssetId);

	const tabItems = useMemo(
		() => [
			{
				id: 0,
				name: DEFAULT_OFFER_SUMMARY_TAB,
				children: priceOverviewChildren,
			},
			{
				id: 1,
				name: 'tripOverview',
				children: tripOverviewChildren,
			},
		],
		[priceOverviewChildren, tripOverviewChildren],
	);

	return (
		<div className={cx({ [classes.priceOverview]: isTabView })}>
			{isTabView ? (
				<TabsBar
					menuItems={tabItems}
					microCopies={microCopies}
					microCopyPrefix="common."
					onTabChange={onTabChange}
					dataCy="OfferSummaryTabs"
				/>
			) : (
				<div>{children}</div>
			)}

			<div className={classes.availabilityContainer}>
				<Button data-cy="availability-cta" disabled={isCheckingAvailability} onClick={checkAvailability}>
					{microCopies['cta.check_price_and_continue']}
				</Button>
				<Typography variant="bodySm" as="p" className={classes.subText}>
					{isAccoOnlyOffer
						? microCopies['accommodation.booking_summary_acco_only.subText']
						: microCopies['accommodation.booking_summary.subText']}
				</Typography>
				<div className={classes.footerItemList}>
					{tourOperator && asset?.fields?.file.url && (
						<div className={classes.footerItem}>
							<Typography variant="bodySm" as="h5" className={classes.footerItemTitle}>
								{microCopies['offerSummary.tourOperator.title']}
							</Typography>
							<div className={classes.logoContainer}>
								<ImageComponent
									src={asset.fields.file.url}
									alt={`${tourOperator} logo`}
									className={classes.tourOperatorLogo}
									sizes="100vh"
									useLoader="Contentful"
									fill
								/>
							</div>
						</div>
					)}

					<div className={classes.footerItem}>
						<Typography variant="bodySm" as="h5" className={classes.footerItemTitle}>
							{microCopies['offerSummary.partners.title']}
						</Typography>
						<ul className={classes.partnerList}>
							<li>
								<Icon type="anvr" />
							</li>
							<li>
								<Icon type="calamiteitenfonds" />
							</li>
							<li>
								<Icon type="sgr" />
							</li>
							<li>
								<Icon type="ideal" />
							</li>
							<li>
								<Icon type="bancontact" />
							</li>
							<li>
								<Icon type="mastercard" />
							</li>
							<li>
								<Icon type="visa" />
							</li>
						</ul>
						<TrustPilot />
					</div>
				</div>
			</div>
		</div>
	);
};
